import React from 'react';
import { Button, CheckboxGroup, Text, Card } from '@goodrx/matisse';
import { SuggestBlock } from 'app/types/Infermedica';
import { TriageBlockConfig, TriageBlockChildProps } from '../TriageBlock';
import { SenderChatBubble, StyledForm, SavedResponseWrapper } from '../shared';

type SuggestSymptomsData = {
  suggestions: SuggestBlock[];
  responseText?: string;
};

export const code: TriageBlockConfig['code'] = 'suggestSymptoms';

export const codePostfix: TriageBlockConfig['codePostfix'] = true;

export const Message: TriageBlockConfig<SuggestSymptomsData>['Message'] = ({ dataValue }) => {
  const suggestions = dataValue?.suggestions;

  return (
    <>
      {suggestions?.length ? (
        <SenderChatBubble>Are you experiencing any of the following symptoms?</SenderChatBubble>
      ) : null}
    </>
  );
};

export const Response: TriageBlockConfig<SuggestSymptomsData, string[]>['Response'] = ({
  inputValue,
  setCurrentInputValue,
  dataValue,
  onSubmit,
  addBlock,
}) => {
  const suggestions = dataValue?.suggestions;

  if (!suggestions?.length) {
    addBlock('evaluate', { prompt: { conditions: [] } });
    return null;
  }

  return (
    <StyledForm onSubmit={onSubmit}>
      <Card>
        <Card.Body style={{ paddingBottom: 0 }} data-qa="symptom-choice-list">
          <CheckboxGroup
            id="suggestSymptoms"
            value={inputValue}
            onChange={(_: any, value: string[]) => setCurrentInputValue(value)}
            choices={suggestions.map((suggestion) => ({
              id: suggestion.id,
              value: suggestion.id,
              label: suggestion.commonName,
            }))}
          />
        </Card.Body>
      </Card>

      <Button data-qa="symptom-submit-btn" variant={inputValue?.length ? 'primary' : 'secondary'} type="submit" mt={2}>
        {!inputValue?.length ? 'None of the above' : 'Submit'}
      </Button>
    </StyledForm>
  );
};

export const onSubmit: TriageBlockConfig<SuggestSymptomsData, string[]>['onSubmit'] = async ({
  blockCode,
  inputValue,
  setCurrentDataValue,
  setDataValue,
  dataValue,
  addBlock,
}) => {
  // Any selected related symptoms are set as evidence
  if (inputValue?.length > 0) {
    setDataValue('evidence', {
      [blockCode]: inputValue.map(
        (id: string) =>
          ({
            id,
            choiceId: 'present',
            source: 'suggest',
          } as const)
      ),
    });
  }

  if (dataValue?.suggestions?.length > 0) {
    const responseText = dataValue.suggestions
      .filter((suggestion) => inputValue && inputValue.includes(suggestion.id))
      .map((suggestion) => suggestion.commonName)
      .join(', ');

    setCurrentDataValue({
      responseText: responseText || 'None of the above',
    } as SuggestSymptomsData);
  }

  addBlock('diagnose');
};

export const SavedResponse: React.FC<TriageBlockChildProps<SuggestSymptomsData>> = ({ dataValue, ...props }) =>
  dataValue?.responseText ? (
    <SavedResponseWrapper {...props}>
      <Text>{dataValue.responseText}</Text>
    </SavedResponseWrapper>
  ) : null;
